import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserCircle, SignOut, GoogleLogo, Car, Clock } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "border-2 border-ink rounded-none h-12 bg-white";
const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };

export default function Account() {
  const { user, loading, login, logout, updateProfile, token, requestOtp, verifyOtp } = useAuth();
  const [p, setP] = useState({ name: "", phone: "", home_address: "", work_address: "", driver_notes: "" });
  const [saving, setSaving] = useState(false);
  const [trips, setTrips] = useState([]);
  const [otpStep, setOtpStep] = useState("email"); // "email" | "code"
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  useEffect(() => {
    if (user) {
      setP({ name: user.name || "", phone: user.phone || "", home_address: user.home_address || "", work_address: user.work_address || "", driver_notes: user.driver_notes || "" });
      axios.get(`${API}/me/bookings`, { headers: { Authorization: `Bearer ${token()}` } })
        .then(({ data }) => setTrips(data.bookings || [])).catch(() => {});
    }
  }, [user, token]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await updateProfile(p); toast.success("Profile saved!"); }
    catch { toast.error("Couldn't save. Try again."); }
    finally { setSaving(false); }
  };

  if (loading) return <main className="min-h-screen bg-paper pt-28 flex justify-center" data-testid="account-loading"><p className="text-ink/50">Loading…</p></main>;

  const errMsg = (e) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    return "Something went wrong. Please try again.";
  };

  const sendCode = async (e) => {
    if (e) e.preventDefault();
    if (!otpEmail.trim() || resendIn > 0) return;
    setOtpBusy(true);
    try {
      await requestOtp(otpEmail.trim(), "");
      setOtpStep("code");
      setResendIn(45);
      toast.success("We emailed you a 6-digit code.");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setOtpBusy(false); }
  };

  const confirmCode = async (e) => {
    e.preventDefault();
    if (otpCode.trim().length < 6) return;
    setOtpBusy(true);
    try {
      await verifyOtp(otpEmail.trim(), otpCode.trim());
      toast.success("Signed in!");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setOtpBusy(false); }
  };

  if (!user) {
    return (
      <main className="min-h-screen bg-ink flex items-center justify-center px-4" data-testid="account-signin">
        <div className="w-full max-w-sm bg-white border-2 border-sun p-8 shadow-hard text-center">
          <UserCircle weight="fill" size={56} className="text-brandblue mx-auto" />
          <h1 className="font-display font-extrabold text-3xl tracking-tight text-ink mt-3">Your account</h1>
          <p className="text-ink/60 text-sm mt-2 mb-6">Sign in to save your details and see all your trips.</p>

          {otpStep === "email" ? (
            <form onSubmit={sendCode} data-testid="otp-email-form" className="text-left mb-4">
              <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/50">Email me a sign-in code</label>
              <Input data-testid="otp-email-input" type="email" required value={otpEmail}
                onChange={(e) => setOtpEmail(e.target.value)} placeholder="you@email.com"
                className="border-2 border-ink rounded-none h-12 bg-white mt-2" />
              <button data-testid="otp-send-btn" type="submit" disabled={otpBusy}
                className="w-full mt-3 bg-sun text-ink font-bold py-3.5 border-2 border-ink shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60">
                {otpBusy ? "Sending…" : "Email me a code"}
              </button>
            </form>
          ) : (
            <form onSubmit={confirmCode} data-testid="otp-code-form" className="text-left mb-4">
              <label className="text-[11px] font-bold uppercase tracking-[0.18em] text-ink/50">Enter the 6-digit code sent to {otpEmail}</label>
              <Input data-testid="otp-code-input" inputMode="numeric" maxLength={6} required value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••"
                className="border-2 border-ink rounded-none h-12 bg-white mt-2 text-center text-2xl tracking-[0.4em]" />
              <button data-testid="otp-verify-btn" type="submit" disabled={otpBusy}
                className="w-full mt-3 bg-ink text-white font-bold py-3.5 border-2 border-ink hover:bg-brandblue transition-colors disabled:opacity-60">
                {otpBusy ? "Verifying…" : "Sign in"}
              </button>
              <div className="flex items-center justify-between mt-2">
                <button type="button" data-testid="otp-back-btn" onClick={() => { setOtpStep("email"); setOtpCode(""); setResendIn(0); }}
                  className="text-xs font-bold text-ink/50 hover:text-ink">Use a different email</button>
                <button type="button" data-testid="otp-resend-btn" onClick={() => sendCode()} disabled={resendIn > 0 || otpBusy}
                  className="text-xs font-bold text-brandblue hover:text-ink disabled:text-ink/40 disabled:cursor-not-allowed">
                  {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
                </button>
              </div>
            </form>
          )}

          <div className="flex items-center gap-3 my-4">
            <div className="h-px bg-ink/15 flex-1" /><span className="text-[11px] font-bold text-ink/40">OR</span><div className="h-px bg-ink/15 flex-1" />
          </div>

          <button data-testid="google-signin-btn" onClick={login}
            className="w-full flex items-center justify-center gap-2 bg-white text-ink font-bold py-3.5 border-2 border-ink hover:bg-brandblue hover:text-white transition-colors">
            <GoogleLogo weight="bold" size={20} /> Continue with Google
          </button>
          <a href="/" data-testid="continue-guest-btn" className="block mt-3 text-sm font-bold text-ink/60 hover:text-ink">Continue as guest →</a>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-paper pt-28 pb-20 px-4 sm:px-8" data-testid="account-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            {user.picture ? <img src={user.picture} alt="" className="w-12 h-12 rounded-full border-2 border-ink" /> : <UserCircle weight="fill" size={48} className="text-ink" />}
            <div>
              <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-ink leading-none">{user.name || "My account"}</h1>
              <p className="text-ink/50 text-sm mt-1">{user.email}</p>
            </div>
          </div>
          <button data-testid="account-signout" onClick={logout} className="flex items-center gap-2 bg-white text-ink font-bold px-4 py-2.5 border-2 border-ink hover:bg-brandred hover:text-white transition-colors">
            <SignOut weight="bold" size={18} /> Sign out
          </button>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <form data-testid="profile-form" onSubmit={save} className="bg-white border-2 border-ink p-6">
            <h2 className="font-display font-extrabold text-xl tracking-tight text-ink mb-4">My details</h2>
            <div className="space-y-4">
              <Input data-testid="profile-name" value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} placeholder="Full name" className={inputCls} />
              <Input data-testid="profile-phone" value={p.phone} onChange={(e) => setP({ ...p, phone: e.target.value })} placeholder="Phone (with area code)" className={inputCls} />
              <Input data-testid="profile-address" value={p.home_address} onChange={(e) => setP({ ...p, home_address: e.target.value })} placeholder="Home address" className={inputCls} />
              <Input data-testid="profile-work" value={p.work_address} onChange={(e) => setP({ ...p, work_address: e.target.value })} placeholder="Work address" className={inputCls} />
              <Textarea data-testid="profile-notes" value={p.driver_notes} onChange={(e) => setP({ ...p, driver_notes: e.target.value })} rows={3} placeholder="Notes for your driver (buzzer code, gate, meeting point…)" className="border-2 border-ink rounded-none" />
            </div>
            <button data-testid="profile-save" type="submit" disabled={saving} className="mt-5 w-full bg-sun text-ink font-bold py-3.5 border-2 border-ink shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all disabled:opacity-60">
              {saving ? "Saving…" : "Save profile"}
            </button>
          </form>

          <div data-testid="my-trips" className="bg-white border-2 border-ink p-6">
            <h2 className="font-display font-extrabold text-xl tracking-tight text-ink mb-4 flex items-center gap-2"><Clock weight="fill" size={20} /> My trips</h2>
            {trips.length === 0 ? (
              <p className="text-ink/50 text-sm" data-testid="trips-empty">No trips yet. Book your first ride!</p>
            ) : (
              <div className="space-y-3 max-h-[420px] overflow-auto">
                {trips.map((t, i) => (
                  <div key={t.id || i} data-testid={`trip-${i}`} className="border-2 border-ink/10 p-3 flex items-start gap-3">
                    <span className="w-9 h-9 bg-sun text-ink flex items-center justify-center shrink-0"><Car weight="fill" size={18} /></span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-ink text-sm">{t.service_type}</p>
                      <p className="text-ink/50 text-xs">{fmt(t.created_at)} · {[t.pickup, t.dropoff].filter(Boolean).join(" → ") || "—"}</p>
                      <p className="text-xs mt-1"><span className="font-bold">{money(t.total)}</span> <span className="text-ink/50">· {t.status}</span></p>
                      <button data-testid={`rebook-${i}`} onClick={() => {
                        localStorage.setItem("ds_rebook", JSON.stringify({ service_type: t.service_type, pickup: t.pickup || "", dropoff: t.dropoff || "", notes: t.notes || "" }));
                        window.location.href = "/";
                      }} className="mt-2 text-xs font-bold text-brandblue hover:text-ink underline underline-offset-2">
                        Book again →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
